import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { User, Mail, Shield, Loader2, Key, Send, CheckCircle2, XCircle, Eye, EyeOff, Trash2, Building2 } from 'lucide-react';

export const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Email configuration state (Super Admin only)
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [resendApiKey, setResendApiKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingEmailConfig, setSavingEmailConfig] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  
  // Advertiser Email Configuration state
  const [advEmailConfig, setAdvEmailConfig] = useState(null);
  const [advEmailConfigLoading, setAdvEmailConfigLoading] = useState(false);
  const [advResendApiKey, setAdvResendApiKey] = useState('');
  const [advSenderEmail, setAdvSenderEmail] = useState('');
  const [showAdvApiKey, setShowAdvApiKey] = useState(false);
  const [savingAdvEmailConfig, setSavingAdvEmailConfig] = useState(false);
  const [testingAdvEmail, setTestingAdvEmail] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdvertiser = user?.role === 'advertiser';

  // Fetch email configuration on mount for Super Admin
  useEffect(() => {
    if (isSuperAdmin) {
      fetchEmailConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);
  
  // Fetch advertiser email configuration on mount for Advertisers
  useEffect(() => {
    if (isAdvertiser) {
      fetchAdvEmailConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdvertiser]);
  
  const fetchAdvEmailConfig = async () => {
    setAdvEmailConfigLoading(true);
    try {
      const response = await api.get('/advertiser-email-config');
      setAdvEmailConfig(response.data);
      if (response.data.sender_email) {
        setAdvSenderEmail(response.data.sender_email);
      }
    } catch (error) {
      console.error('Failed to fetch advertiser email config:', error);
    } finally {
      setAdvEmailConfigLoading(false);
    }
  };
  
  const handleSaveAdvEmailConfig = async (e) => {
    e.preventDefault();
    
    if (!advResendApiKey.trim()) {
      toast.error('Please enter a Resend API key');
      return;
    }
    
    if (!advSenderEmail.trim()) {
      toast.error('Please enter your sender email address');
      return;
    }
    
    setSavingAdvEmailConfig(true);
    try {
      const response = await api.put('/advertiser-email-config', {
        api_key: advResendApiKey,
        sender_email: advSenderEmail
      });
      
      setAdvEmailConfig(response.data);
      setAdvResendApiKey('');
      toast.success('Email configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email configuration');
    } finally {
      setSavingAdvEmailConfig(false);
    }
  };
  
  const handleDeleteAdvEmailConfig = async () => {
    if (!window.confirm('Remove your custom email configuration? Alerts will be sent from the system default email.')) {
      return;
    }
    
    setSavingAdvEmailConfig(true);
    try {
      await api.delete('/advertiser-email-config');
      setAdvEmailConfig({ configured: false });
      setAdvSenderEmail('');
      toast.success('Email configuration removed');
    } catch (error) {
      toast.error('Failed to remove email configuration');
    } finally {
      setSavingAdvEmailConfig(false);
    }
  };
  
  const handleTestAdvEmail = async () => {
    setTestingAdvEmail(true);
    try {
      const response = await api.post('/advertiser-email-config/test');
      toast.success(response.data.message || 'Test email sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingAdvEmail(false);
    }
  };

  const fetchEmailConfig = async () => {
    setEmailConfigLoading(true);
    try {
      const response = await api.get('/email-config-status');
      setEmailConfig(response.data);
      if (response.data.sender_email) {
        setSenderEmail(response.data.sender_email);
      }
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    } finally {
      setEmailConfigLoading(false);
    }
  };

  const handleSaveEmailConfig = async (e) => {
    e.preventDefault();
    
    if (!resendApiKey.trim()) {
      toast.error('Please enter a Resend API key');
      return;
    }
    
    setSavingEmailConfig(true);
    try {
      const response = await api.put('/email-config', {
        api_key: resendApiKey,
        sender_email: senderEmail || null
      });
      
      setEmailConfig(response.data);
      setResendApiKey('');
      toast.success('Email configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email configuration');
    } finally {
      setSavingEmailConfig(false);
    }
  };

  const handleDeleteEmailConfig = async () => {
    if (!window.confirm('Remove custom email configuration? The system will revert to using environment variables if available.')) {
      return;
    }
    
    setSavingEmailConfig(true);
    try {
      await api.delete('/email-config');
      await fetchEmailConfig();
      toast.success('Custom email configuration removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove email configuration');
    } finally {
      setSavingEmailConfig(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await api.post('/email-config/test');
      toast.success(response.data.message || 'Test email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulating save action
    setTimeout(() => {
      toast.success('Settings saved successfully');
      setLoading(false);
    }, 500);
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn max-w-2xl">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your account settings</p>
        </div>

        {/* Profile Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Profile Information</CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  Full Name
                </Label>
                <Input
                  id="name"
                  defaultValue={user?.name}
                  data-testid="settings-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  defaultValue={user?.email}
                  data-testid="settings-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-slate-500" />
                  Role
                </Label>
                <Input
                  value={user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  disabled
                  className="bg-slate-50"
                />
              </div>

              <div className="pt-4">
                <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="settings-save-btn">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Email Configuration Card - Super Admin Only */}
        {isSuperAdmin && (
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email Configuration
                  </CardTitle>
                  <CardDescription>Configure Resend API for email reports</CardDescription>
                </div>
                {emailConfig && (
                  <Badge 
                    variant={emailConfig.configured ? 'default' : 'secondary'}
                    className={emailConfig.configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
                  >
                    {emailConfig.configured ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Configured
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Not Configured
                      </>
                    )}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {emailConfigLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Current Configuration Status */}
                  {emailConfig?.configured && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Current Configuration</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Status</span>
                          <span className="text-green-600 font-medium">Active</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Source</span>
                          <Badge variant="outline" className="text-xs">
                            {emailConfig.source === 'database' ? 'Custom (Database)' : 'Environment Variable'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">API Key</span>
                          <span className="font-mono text-slate-700">{emailConfig.masked_key}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Sender Email</span>
                          <span className="text-slate-700">{emailConfig.sender_email}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleTestEmail}
                          disabled={testingEmail}
                          data-testid="test-email-btn"
                        >
                          {testingEmail ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Send Test Email
                        </Button>
                        
                        {emailConfig.source === 'database' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDeleteEmailConfig}
                            disabled={savingEmailConfig}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="remove-email-config-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove Custom Config
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Configuration Form */}
                  <form onSubmit={handleSaveEmailConfig} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="resendApiKey" className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-slate-500" />
                        Resend API Key
                      </Label>
                      <div className="relative">
                        <Input
                          id="resendApiKey"
                          type={showApiKey ? 'text' : 'password'}
                          value={resendApiKey}
                          onChange={(e) => setResendApiKey(e.target.value)}
                          placeholder={emailConfig?.configured ? 'Enter new API key to update...' : 'Enter your Resend API key...'}
                          className="pr-10"
                          data-testid="resend-api-key-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setShowApiKey(!showApiKey)}
                        >
                          {showApiKey ? (
                            <EyeOff className="w-4 h-4 text-slate-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-slate-400" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Get your API key from{' '}
                        <a 
                          href="https://resend.com/api-keys" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          resend.com/api-keys
                        </a>
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="senderEmail" className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-500" />
                        Sender Email (Optional)
                      </Label>
                      <Input
                        id="senderEmail"
                        type="email"
                        value={senderEmail}
                        onChange={(e) => setSenderEmail(e.target.value)}
                        placeholder="reports@yourdomain.com"
                        data-testid="sender-email-input"
                      />
                      <p className="text-xs text-slate-500">
                        Leave empty to use default. For custom domains, verify your domain in Resend first.
                      </p>
                    </div>
                    
                    <div className="pt-2">
                      <Button 
                        type="submit" 
                        disabled={savingEmailConfig || !resendApiKey.trim()} 
                        className="bg-slate-900 hover:bg-slate-800"
                        data-testid="save-email-config-btn"
                      >
                        {savingEmailConfig ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Key className="w-4 h-4 mr-2" />
                            {emailConfig?.configured ? 'Update Configuration' : 'Save Configuration'}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Advertiser Email Configuration Card - Advertiser Only */}
        {isAdvertiser && (
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Email Configuration
                  </CardTitle>
                  <CardDescription>Send campaign alerts from your own domain email</CardDescription>
                </div>
                {advEmailConfig && (
                  <Badge 
                    variant={advEmailConfig.configured ? 'default' : 'secondary'}
                    className={advEmailConfig.configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
                  >
                    {advEmailConfig.configured ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Configured
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Using Default
                      </>
                    )}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {advEmailConfigLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : advEmailConfig?.configured ? (
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Sender Email</span>
                      <span className="font-medium text-slate-900">{advEmailConfig.sender_email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">API Key</span>
                      <span className="font-mono text-slate-700">{advEmailConfig.masked_key}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleTestAdvEmail}
                      disabled={testingAdvEmail}
                      className="flex-1"
                      data-testid="test-adv-email-btn"
                    >
                      {testingAdvEmail ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Test Email
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDeleteAdvEmailConfig}
                      disabled={savingAdvEmailConfig}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="delete-adv-email-config-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <p className="text-xs text-slate-500">
                    Campaign alerts will be sent from {advEmailConfig.sender_email}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Why configure your own email?</strong><br />
                      Send campaign alerts from your own domain (e.g., alerts@yourcompany.com) instead of the system default. 
                      This improves deliverability and looks more professional to your team.
                    </p>
                  </div>
                  
                  <form onSubmit={handleSaveAdvEmailConfig} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="advSenderEmail" className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-500" />
                        Sender Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="advSenderEmail"
                        type="email"
                        value={advSenderEmail}
                        onChange={(e) => setAdvSenderEmail(e.target.value)}
                        placeholder="alerts@yourcompany.com"
                        required
                        data-testid="adv-sender-email-input"
                      />
                      <p className="text-xs text-slate-500">
                        The email address alerts will be sent from. Must be verified in Resend.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="advResendApiKey" className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-slate-500" />
                        Resend API Key <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="advResendApiKey"
                          type={showAdvApiKey ? 'text' : 'password'}
                          value={advResendApiKey}
                          onChange={(e) => setAdvResendApiKey(e.target.value)}
                          placeholder="re_xxxxxxxxx..."
                          required
                          className="pr-10"
                          data-testid="adv-resend-api-key-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setShowAdvApiKey(!showAdvApiKey)}
                        >
                          {showAdvApiKey ? (
                            <EyeOff className="w-4 h-4 text-slate-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-slate-400" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Get your API key from{' '}
                        <a 
                          href="https://resend.com/api-keys" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          resend.com/api-keys
                        </a>
                        . Your domain must be verified in Resend.
                      </p>
                    </div>
                    
                    <div className="pt-2">
                      <Button 
                        type="submit" 
                        disabled={savingAdvEmailConfig || !advResendApiKey.trim() || !advSenderEmail.trim()} 
                        className="bg-slate-900 hover:bg-slate-800"
                        data-testid="save-adv-email-config-btn"
                      >
                        {savingAdvEmailConfig ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Key className="w-4 h-4 mr-2" />
                            Save Configuration
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Account Info Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Account Information</CardTitle>
            <CardDescription>Details about your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Account ID</span>
                <span className="font-mono text-slate-900">{user?.id?.slice(0, 8)}...</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Account Status</span>
                <span className={`font-medium ${user?.is_active ? 'text-emerald-600' : 'text-red-600'}`}>
                  {user?.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Member Since</span>
                <span className="text-slate-900">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }) : '—'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
